import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Sparkles, Users, CheckCircle2, PartyPopper, Calendar, Mail } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { EventBooking } from '../../types';

export const PrivateBookingSection: React.FC = () => {
  const { lang } = useLanguage();

  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    eventType: 'birthday' as EventBooking['eventType'],
    guests: '10',
    preferredDate: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const text = {
    eyebrow: {
      fi: 'YKSITYISTILAISUUDET / PRIVATE BOOKINGS',
      en: 'PRIVATE BOOKINGS & CELEBRATIONS',
      sv: 'PRIVATA TILLSTÄLLNINGAR & FEST'
    },
    title: {
      fi: 'Juhlat, tiimi-illat ja kabinettivaraukset',
      en: 'Celebrations, Corporate Dinners & Private Hire',
      sv: 'Fester, företagsmiddagar och kabinettbokning'
    },
    desc: {
      fi: 'Suunnitteletko syntymäpäivää, yrityksen iltajuhlaa, pikkujouluja tai kabinetti-illallista? Koko ravintolamme tai tunnelmallinen kabinettimme on varattavissa yksityiskäyttöön jopa 90 hengelle. Räätälöimme puu-uunin menukokonaisuudet ja juomat toiveidenne mukaan.',
      en: 'Planning an intimate milestone birthday, corporate team night, or a full venue buyout for up to 90 guests? We craft multi-course wood-fired menus, pair exquisite wines and cocktails, and provide dedicated hospitality so you can focus on celebrating.',
      sv: 'Planerar du födelsedag, representation eller privat fest? Vår lokal eller kabinett kan bokas för upp till 90 personer med skräddarsydda menyer och personlig service.'
    },
    formTitle: {
      fi: 'Lähetä Tapahtumakysely',
      en: 'Send Private Event Enquiry',
      sv: 'Skicka Förfrågan för Event'
    },
    formDesc: {
      fi: 'Kysely toimitetaan suoraan ravintolapäällikölle (asiakaspalvelu@theovenstory.fi). Vastaamme 24h sisällä.',
      en: 'Enquiries are routed directly to management (asiakaspalvelu@theovenstory.fi). We respond within 24h.',
      sv: 'Förfrågan sänds direkt till restaurangledningen (asiakaspalvelu@theovenstory.fi). Svar inom 24h.'
    },
    btnSubmit: {
      fi: 'Lähetä Kysely →',
      en: 'Submit Enquiry →',
      sv: 'Skicka Förfrågan →'
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const bookingPayload: EventBooking = {
      id: `evt_${Date.now()}`,
      fullName: form.fullName.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      eventType: form.eventType,
      guests: Number(form.guests) || 4,
      preferredDate: form.preferredDate,
      message: form.message.trim(),
      status: 'pending',
      staffNotes: '',
      submittedAt: new Date().toISOString()
    };

    try {
      await apiService.saveEventBooking(bookingPayload);
    } catch (err) {
      console.warn('Could not persist booking via apiService:', err);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  };

  return (
    <section id="private-booking" className="os-page-section alt-bg">
      <div className="os-container">
        <div className="os-booking-layout">
          {/* Left Column: Storytelling & Visual */}
          <div>
            <div className="os-section-header" style={{ marginBottom: '24px' }}>
              <span className="os-eyebrow">{text.eyebrow[lang]}</span>
              <h2 className="os-section-title">{text.title[lang]}</h2>
              <p className="os-section-desc">{text.desc[lang]}</p>
            </div>

            <div className="os-feature-media" style={{ aspectRatio: '16 / 10' }}>
              <img
                src="/images/private-event-celebration.jpg"
                alt="Private dining and event celebration at The Oven Story Helsinki"
              />
            </div>
          </div>

          {/* Right Column: Clean Direct Enquiry Form */}
          <div className="os-booking-form">
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '36px 12px' }}>
                <div style={{ display: 'inline-flex', padding: '16px', borderRadius: '50%', background: 'rgba(224,90,54,0.15)', color: 'var(--os-copper)', marginBottom: '16px' }}>
                  <PartyPopper size={40} />
                </div>
                <h3 className="os-font-serif" style={{ fontSize: '2rem', color: '#ffffff', marginBottom: '10px' }}>
                  {lang === 'fi' ? 'Kysely Vastaanotettu!' : 'Enquiry Received!'}
                </h3>
                <p style={{ color: 'var(--os-text-secondary)', fontSize: '0.95rem', marginBottom: '24px' }}>
                  {lang === 'fi'
                    ? `Kiitos yhteydenotostasi, ${form.fullName}! Kyselysi on tallennettu ja toimitettu ravintolatiimille (asiakaspalvelu@theovenstory.fi). Ravintolapäällikkömme ottaa sinuun yhteyttä sähköpostitse (${form.email}) tai puhelimitse (${form.phone}) 24 tunnin sisällä.`
                    : `Thank you for reaching out, ${form.fullName}! Your enquiry has been received and routed to management (asiakaspalvelu@theovenstory.fi). We will contact you at ${form.email} or by phone (${form.phone}) within 24 hours.`}
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="os-btn-hero-secondary"
                >
                  {lang === 'fi' ? 'Lähetä uusi kysely' : 'Send another enquiry'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <h3 className="os-font-serif" style={{ fontSize: '1.8rem', color: '#ffffff', margin: '0 0 6px 0' }}>
                    {text.formTitle[lang]}
                  </h3>
                  <p style={{ color: 'var(--os-text-muted)', fontSize: '0.88rem', margin: 0 }}>
                    {text.formDesc[lang]}
                  </p>
                </div>

                <div className="os-form-group">
                  <label className="os-label">
                    {lang === 'fi' ? 'Nimi' : 'Full Name'} <span className="req">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="os-input"
                    placeholder="Esim. Matti Virtanen"
                  />
                </div>

                <div className="os-form-row">
                  <div className="os-form-group">
                    <label className="os-label">
                      {lang === 'fi' ? 'Sähköposti' : 'Email'} <span className="req">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="os-input"
                      placeholder="matti@yritys.fi"
                    />
                  </div>

                  <div className="os-form-group">
                    <label className="os-label">
                      {lang === 'fi' ? 'Puhelin' : 'Phone'} <span className="req">*</span>
                    </label>
                    <input
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="os-input"
                      placeholder="+358 40 555 8920"
                    />
                  </div>
                </div>

                <div className="os-form-row">
                  <div className="os-form-group">
                    <label className="os-label">
                      {lang === 'fi' ? 'Tilaisuuden Tyyppi' : 'Event Type'} <span className="req">*</span>
                    </label>
                    <select
                      value={form.eventType}
                      onChange={(e) => setForm({ ...form, eventType: e.target.value as EventBooking['eventType'] })}
                      className="os-select"
                    >
                      <option value="birthday">Syntymäpäivä / Birthday</option>
                      <option value="corporate">Yritystapahtuma / Corporate</option>
                      <option value="team">Tiimi-ilta / Team Dinner</option>
                      <option value="cabinet">Kabinettivaraus / Private Room</option>
                      <option value="buyout">Koko Ravintolan Vuokraus / Buyout</option>
                    </select>
                  </div>

                  <div className="os-form-group">
                    <label className="os-label">
                      {lang === 'fi' ? 'Henkilömäärä' : 'Guests'} <span className="req">*</span>
                    </label>
                    <input
                      type="number"
                      min="4"
                      max="100"
                      required
                      value={form.guests}
                      onChange={(e) => setForm({ ...form, guests: e.target.value })}
                      className="os-input"
                    />
                  </div>
                </div>

                <div className="os-form-group">
                  <label className="os-label">
                    {lang === 'fi' ? 'Toivottu Päivämäärä' : 'Preferred Date'} <span className="req">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    value={form.preferredDate}
                    onChange={(e) => setForm({ ...form, preferredDate: e.target.value })}
                    className="os-input"
                  />
                </div>

                <div className="os-form-group">
                  <label className="os-label">
                    {lang === 'fi' ? 'Lisätiedot & Toiveet' : 'Special Notes & Wishes'}
                  </label>
                  <textarea
                    rows={3}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="os-textarea"
                    placeholder={lang === 'fi' ? 'Kerro toiveistasi koskien menua, aikataulua tai juomapaketteja...' : 'Tell us about dietary needs, schedule, or drink packages...'}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="os-btn-hero-primary"
                  style={{ width: '100%', justifyContent: 'center', marginTop: '8px' }}
                >
                  <Sparkles size={18} />
                  <span>{loading ? 'Lähetetään...' : text.btnSubmit[lang]}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
